/**
 * Glossário central de tooltips para KPIs/indicadores.
 * Use `getKpiTooltip(label)` para obter a descrição padronizada a partir do rótulo exibido na UI.
 */

const kpiTooltips: Record<string, string> = {
  // Negócio / GA4
  "Receita Total":
    "Soma da receita do e-commerce no período (vendas concluídas). Útil para medir crescimento e impacto das ações de marketing.",
  Receita:
    "Receita do e-commerce no período. Em dashboards de Ads, normalmente representa a receita atribuída às campanhas (conforme atribuição/configuração).",
  "Receita (META)":
    "Receita atribuída às campanhas do Meta Ads no período (conforme modelo de atribuição/configuração). Use para avaliar retorno das campanhas da Meta.",
  "Receita (Google)":
    "Receita atribuída às campanhas do Google Ads no período (conforme modelo de atribuição/configuração). Use para avaliar retorno das campanhas do Google.",
  Investimento:
    "Valor total investido em mídia paga no período. Em geral, é o gasto (spend/cost) de Google Ads e/ou Meta Ads.",
  Sessões:
    "Número de visitas (sessões) no site no período. Uma sessão pode ter várias páginas e interações (GA4).",
  Usuários:
    "Número de pessoas (usuários) que visitaram o site no período (GA4). Um usuário pode ter várias sessões.",
  "Usuários Únicos":
    "Número de usuários distintos no período (GA4). Útil para estimar alcance real do site, diferente de sessões (que podem repetir o mesmo usuário).",
  Pedidos:
    "Quantidade de compras/transações concluídas no período (GA4 / e-commerce).",
  Compras:
    "Quantidade de compras/transações concluídas no período. Em Ads, pode refletir conversões de compra configuradas na conta.",
  Transações:
    "Quantidade de compras/transações concluídas no período (GA4 / e-commerce).",
  Conversões:
    "Total de ações de conversão registradas (ex.: compra, lead). Em Ads, segue a configuração de conversões da conta.",
  "Ticket Médio":
    "Valor médio por pedido (AOV): receita total ÷ número de pedidos. Ajuda a entender qualidade do faturamento.",
  "Valor Médio do Pedido":
    "Valor médio por pedido (AOV): receita total ÷ número de pedidos. Ajuda a entender qualidade do faturamento.",
  TM: "Ticket médio (AOV): receita total ÷ número de pedidos. Ajuda a entender qualidade do faturamento.",
  "Taxa Conversão":
    "Percentual de visitas que viraram compra: pedidos ÷ sessões. Ajuda a avaliar eficiência do site e da oferta.",
  "Taxa de Conversão":
    "Percentual de visitas que viraram compra: pedidos ÷ sessões. Ajuda a avaliar eficiência do site e da oferta.",
  "Taxa de Conversão E-commerce":
    "Percentual de visitas que viraram compra: pedidos ÷ sessões (GA4). Indica eficiência do site e do checkout para converter tráfego em vendas.",

  // Produtos
  "Receita do Item":
    "Receita total gerada pelos itens/produtos vendidos no período (GA4 e-commerce). Útil para acompanhar faturamento por produto.",
  "Itens Comprados":
    "Quantidade total de unidades vendidas (itens comprados) no período. Difere de 'Pedidos' (um pedido pode ter vários itens).",
  "Qtd. Média Itens":
    "Quantidade média de itens por pedido: itens comprados ÷ pedidos. Ajuda a entender oportunidade de upsell/cross-sell.",
  "Valor Médio Unit.":
    "Preço médio por unidade vendida: receita do item ÷ itens comprados. Ajuda a acompanhar mix de produtos e descontos.",
  "Média Itens P/ Pedido":
    "Quantidade média de itens por pedido: itens comprados ÷ pedidos. Ajuda a entender oportunidade de upsell/cross-sell.",
  "Valor Médio Unitário":
    "Preço médio por unidade vendida: receita do item ÷ itens comprados. Ajuda a acompanhar mix de produtos e descontos.",
  "TX Conversion":
    "Percentual de visitas que viraram compra: pedidos ÷ sessões. Ajuda a avaliar eficiência do site e da oferta.",
  "% Marketing":
    "Percentual da receita comprometida com marketing: investimento ÷ receita. Quanto menor (mantendo crescimento), melhor a eficiência.",
  "Marketing %":
    "Percentual da receita comprometida com marketing: investimento ÷ receita. Quanto menor (mantendo crescimento), melhor a eficiência.",
  ROAS: "Retorno sobre investimento em anúncios: receita atribuída ÷ investimento. Ex.: 4,0 = R$4 retornam a cada R$1 investido.",

  // Ads (geral)
  "Gasto Total":
    "Total gasto em anúncios no período (spend/cost). Use para controlar orçamento e comparar com resultado (conversões/receita/ROAS).",
  Impressões:
    "Quantidade de vezes que o anúncio foi exibido. Não significa pessoas únicas (para isso, use Alcance).",
  Cliques:
    "Total de cliques gerados pelos anúncios no período. Dependendo da plataforma, pode incluir cliques gerais (não só no link).",
  "Total de Cliques":
    "Total de cliques (geralmente orgânicos no Google quando vindo do Search Console). Indica tráfego de busca gerado pelos seus resultados.",
  "Cliques Totais":
    "Soma de cliques pagos e orgânicos (quando a página consolida fontes). Bom para medir demanda e tráfego gerado.",
  CTR: "Taxa de cliques: cliques ÷ impressões. Indica atratividade do anúncio/criativo e aderência à intenção do público.",
  "CTR Médio":
    "Taxa média de cliques: cliques ÷ impressões. Indica atratividade do anúncio/criativo e aderência à intenção do público.",
  CPC: "Custo por clique: investimento ÷ cliques. Ajuda a controlar eficiência para gerar tráfego.",
  "CPC Médio":
    "Custo médio por clique: investimento ÷ cliques. Ajuda a controlar eficiência para gerar tráfego.",
  CPM: "Custo por 1.000 impressões: (investimento ÷ impressões) × 1.000. Indica custo de entrega (principal em Meta).",
  CPA: "Custo por aquisição/conversão: investimento ÷ conversões. Use para controlar custo por venda/lead.",
  "ROAS Médio":
    "Média de ROAS no período (receita atribuída ÷ investimento). Use para comparar eficiência entre períodos/campanhas.",
  "Quality Score":
    "Nota de qualidade do Google Ads (aprox. 1–10) baseada em CTR esperado, relevância do anúncio e experiência da landing page. Pontuações maiores tendem a reduzir CPC e melhorar posição.",
  "Posição Média":
    "Posição média em que seus anúncios aparecem (quando disponível). Em geral, menor número = posição mais alta. Observação: em algumas contas/campanhas essa métrica pode ser substituída por métricas de 'impression share'.",
  Alcance:
    "Número de pessoas únicas que viram o conteúdo/anúncio no período (principalmente em Meta/Instagram).",
  Frequência:
    "Média de quantas vezes cada pessoa foi impactada: impressões ÷ alcance. Frequência alta pode indicar saturação do público.",

  // Funil / métricas derivadas
  CPS: "Custo por sessão: investimento ÷ sessões. Mostra quanto custa gerar uma visita ao site (eficiência de tráfego).",
  "CPS (GA)":
    "Custo por sessão calculado com base nas sessões do Google Analytics: investimento ÷ sessões (GA4). Ajuda a medir eficiência para gerar tráfego qualificado.",
  "CPS GERAL":
    "Custo por sessão consolidado: (investimento total) ÷ (sessões). Ajuda a comparar eficiência entre períodos e canais.",
  "Connect Rate":
    "Taxa de conexão entre anúncios e site: sessões ÷ cliques (ou visitas reais geradas) no período. Indica perda entre clique e carregamento/track.",
  "Sessões > Carrinho":
    "Taxa de avanço no funil: percentual de sessões que adicionaram ao carrinho. Ajuda a avaliar intenção e experiência do produto.",
  "Carrinho > Compra":
    "Taxa de checkout: percentual de carrinhos que viraram compra. Ajuda a avaliar fricção no checkout e condições (frete, pagamento, etc.).",
  "Sessões (GA)":
    "Número de sessões no site no período (GA4). Use para medir volume de tráfego e comparar com conversão/receita.",
  "Sessões Engajadas":
    "Sessões engajadas (GA4): sessões com engajamento (ex.: duração mínima/2+ pageviews/1+ conversão, conforme definição do GA4). Indica qualidade do tráfego.",
  "Taxa Engajamento":
    "Taxa de engajamento (GA4): sessões engajadas ÷ sessões. Indica qualidade do tráfego e relevância das páginas.",

  // Meta Ads / Instagram (interações)
  "CTR (Cliques Link)":
    "CTR de link: cliques no link ÷ impressões. Indica o quanto o criativo incentiva o tráfego para o site.",
  "CPC (Cliques Link)":
    "Custo por clique no link: investimento ÷ cliques no link. Útil para comparar criativos com foco em tráfego.",
  Curtidas:
    "Número de curtidas no conteúdo no período. É um sinal de interesse, mas não garante tráfego ou vendas.",
  Comentários:
    "Número de comentários no conteúdo no período. Indica envolvimento mais profundo e pode sinalizar objeções/dúvidas.",
  Salvos:
    "Número de salvamentos. Sinal forte de valor (conteúdo útil) e costuma correlacionar com consideração.",
  Compartilhamentos:
    "Número de compartilhamentos. Indica potencial de viralização e alcance orgânico adicional.",
  "Taxa de Engajamento":
    "Percentual de pessoas alcançadas que interagiram (curtidas, comentários, salvamentos e compartilhamentos). Indica relevância do conteúdo.",
  "Visitas ao Perfil":
    "Número de visitas ao perfil no período. Bom para medir interesse na marca e efeito de conteúdo/campanhas.",
  "Cliques Link Bio":
    "Cliques no link da bio. Mede intenção de ir ao site/WhatsApp a partir do perfil.",
  "Cliques no Site":
    "Cliques para o site originados do Instagram (perfil/CTA). Indica tráfego social direto.",
  Seguidores:
    "Total atual de seguidores. Ajuda a acompanhar crescimento de audiência ao longo do tempo.",
  "Novos Seguidores":
    "Número de seguidores ganhos no período. Indica tração de conteúdo e/ou campanhas de crescimento.",
  Visualizações:
    "Total de visualizações/impressões do conteúdo no período. É volume de exposição, não pessoas únicas.",
  Engajamento:
    "Interações do público com o conteúdo (curtidas, comentários, salvamentos, compartilhamentos etc.) ou, quando indicado em %, a taxa média de engajamento. Indica relevância do conteúdo.",
  "Visualizações do Perfil":
    "Número de visualizações/visitas ao perfil no período. Bom para medir interesse na marca após conteúdos e campanhas.",

  // SEO / Search Console (orgânico)
  "Pedidos Orgânicos":
    "Quantidade de pedidos atribuídos ao canal orgânico (SEO) no período. Ajuda a medir vendas geradas pela busca orgânica.",
  "Sessões Orgânicas":
    "Sessões vindas de busca orgânica (SEO) no período. Indica volume de tráfego orgânico chegando ao site (GA4).",
  "Taxa Conversão Orgânica":
    "Taxa de conversão do tráfego orgânico: pedidos orgânicos ÷ sessões orgânicas. Mostra qualidade do tráfego SEO e da página de destino.",
  "Total de Clicks (Search Console)":
    "Cliques orgânicos nos resultados do Google (Search Console) no período. Mede tráfego potencial vindo das SERPs.",
  "Total de Clicks MARCA (Search Console)":
    "Cliques orgânicos em buscas de marca (termos que contêm sua marca) no Search Console. Ajuda a separar demanda de marca vs. genérica.",
  "Impressões (Search Console)":
    "Impressões orgânicas no Google (Search Console): quantas vezes suas páginas apareceram nas SERPs no período.",
  "Impressões MARCA (Search Console)":
    "Impressões orgânicas para buscas de marca (Search Console). Indica visibilidade da marca nas buscas.",
};

export function getKpiTooltip(label: string): string | undefined {
  const key = (label || "").trim();
  if (!key) return undefined;

  if (kpiTooltips[key]) return kpiTooltips[key];

  // Heurísticas para variações comuns (sem "quebrar" chaves exatas)
  const upper = key.toUpperCase();

  if (upper === "AOV") return kpiTooltips["Ticket Médio"];
  if (upper.includes("ROAS")) return kpiTooltips["ROAS"];
  if (upper.startsWith("CTR")) return kpiTooltips["CTR"];
  if (upper.startsWith("CPC")) return kpiTooltips["CPC"];
  if (upper === "CPM") return kpiTooltips["CPM"];
  if (upper === "CPA") return kpiTooltips["CPA"];
  if (upper.includes("IMPRESS")) return kpiTooltips["Impressões"];
  if (upper.includes("ALCANCE")) return kpiTooltips["Alcance"];
  if (upper.includes("SESS")) return kpiTooltips["Sessões"];

  return undefined;
}
